import AnchorWithIcon from "atoms/anchors/anchor-with-icon";
import Button, { ButtonTypes } from "atoms/buttons/button";
import CultureResources from "utilities/interfaces/culture-resources";
import Heading from "atoms/typography/heading";
import Icon from "atoms/icons/icon";
import LegacyPublicationRecord from "models/view-models/legacy-publication-record";
import Loader from "atoms/loaders/loader";
import Paragraph from "atoms/typography/paragraph";
import PublicationRecord from "models/view-models/publication-record";
import React, { PropsWithChildren } from "react";
import RemoteImage from "atoms/images/remote-image";
import Tooltip, { TooltipPlacement } from "molecules/tooltips/tooltip";
import UnorderedList from "molecules/lists/unordered-list";
import usePublicationFavorites from "utilities/hooks/domain/user-publication-favorites/use-publication-favorites";
import useUpdatePublicationFavorite from "utilities/hooks/domain/user-publication-favorites/use-update-publication-favorite";
import { BrowserUtils } from "utilities/browser-utils";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { CollectionUtils } from "utilities/collection-utils";
import { HeadingPriority } from "atoms/constants/heading-priority";
import { Icons } from "atoms/constants/icons";
import { LoaderSizes } from "atoms/constants/loader-sizes";
import { LoaderStyles } from "atoms/constants/loader-styles";
import { ParagraphSizes } from "atoms/constants/paragraph-sizes";
import { RouteUtils } from "utilities/route-utils";
import { SkipNavContent } from "@reach/skip-nav";
import { ToastManager } from "utilities/toast/toast-manager";
import { siteMap } from "internal-sitemap";
import { useErrorBoundary } from "utilities/hooks/use-error-boundary";
import { useLocalization } from "utilities/hooks/use-localization";
import FileRecord from "models/view-models/file-record";
import Image from "atoms/images/image";
import MenuButton from "molecules/menu-button/menu-button";
import useModalActions from "utilities/hooks/use-modal-actions";
import ChangeEditionModal from "organisms/modals/change-edition-modal/change-edition-modal";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface TableOfContentMetaDataProps {
    publication?: LegacyPublicationRecord;
    publicationLoaded: boolean;
    pageErrors: string[];
    type: "home" | "back";
    onBackClick?(): void;
    code: string;
    edition: string;
    title: string;
    subTitle?: string;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASSNAME = "c-table-of-content-meta-data";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const TableOfContentMetaData: React.FC<
    PropsWithChildren<TableOfContentMetaDataProps>
> = (props) => {
    const {
        publication,
        publicationLoaded,
        pageErrors,
        type,
        onBackClick,
        code,
        edition,
        title,
        subTitle,
    } = props;

    // -----------------------------------------------------------------------------------------
    // #region Hooks
    // -----------------------------------------------------------------------------------------
    const { t } = useLocalization();

    const {
        handleOpen: handleChangeEditionOpen,
        handleClose: handleChangeEditionClose,
        isOpen: showChangeEditionModal,
    } = useModalActions();

    // #endregion Hooks

    useErrorBoundary(pageErrors);

    const publicationRecord = publication?.toPublicationRecord();

    return (
        <>
            <div className={`${BASE_CLASSNAME}__metadata`}>
                <div className={`${BASE_CLASSNAME}__metadata__top`}>
                    <BackButton type={type} onBackClick={onBackClick} />
                    <div className={`${BASE_CLASSNAME}__metadata__top__right`}>
                        {publicationRecord && (
                            <>
                                <PublicationFavoriteButton
                                    publication={publicationRecord}
                                />

                                <div
                                    className={`${BASE_CLASSNAME}__metadata__top__right__more-menu`}>
                                    <MenuButton>
                                        <Button
                                            onClick={handleChangeEditionOpen}>
                                            {t("changeEdition")}
                                        </Button>
                                    </MenuButton>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {!publicationLoaded && CollectionUtils.isEmpty(pageErrors) && (
                    <Loader
                        accessibleText={t("loadingItem", {
                            item: t("publication"),
                        })}
                        type={LoaderStyles.LinkGlyphGray}
                    />
                )}
                {CollectionUtils.hasValues(pageErrors) && (
                    <UnorderedList
                        cssClassName={`${BASE_CLASSNAME}__errors`}
                        listItems={pageErrors}
                    />
                )}

                <>
                    <PublicationInfo
                        code={code ?? ""}
                        edition={edition ?? ""}
                        title={title ?? ""}
                        subTitle={subTitle ?? ""}
                    />

                    {publicationRecord && (
                        <>
                            <div className="c-publication-page__footer">
                                <Button
                                    cssClassName="-link"
                                    onClick={handleChangeEditionOpen}>
                                    {t("changeEdition")}
                                </Button>
                            </div>

                            <ChangeEditionModal
                                closeDialog={handleChangeEditionClose}
                                currentPublication={publicationRecord}
                                isVisible={showChangeEditionModal}
                            />
                        </>
                    )}
                </>
            </div>

            <div className="c-publication-page__toc">
                <SkipNavContent>{props.children}</SkipNavContent>
            </div>
        </>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default TableOfContentMetaData;

// #endregion Exports

interface BackButtonProps {
    type: "home" | "back";
    onBackClick?(): void;
}

export const BackButton: React.FC<BackButtonProps> = ({
    type = "home",
    onBackClick,
}) => {
    const { t } = useLocalization();
    return type === "back" ? (
        <Button
            size={ButtonSizes.Small}
            style={ButtonStyles.TertiaryAlt}
            onClick={onBackClick}>
            <Icon type={Icons.ChevronLeft} />
            {t("back")}
        </Button>
    ) : (
        <AnchorWithIcon
            accessibleText={t("backToLocation", {
                location: t("home"),
            })}
            icon={Icons.ChevronLeft}
            size={ButtonSizes.Small}
            style={ButtonStyles.TertiaryAlt}
            to={RouteUtils.getUrl(siteMap.dashboards.user)}>
            {t("home")}
        </AnchorWithIcon>
    );
};

interface PublicationFavoriteButtonProps {
    publication: PublicationRecord;
}

export const PublicationFavoriteButton: React.FC<
    PublicationFavoriteButtonProps
> = ({ publication }) => {
    const { t } = useLocalization();

    const handleFavoriteButtonClick = () => {
        if (isFavorited) {
            unfavorite();
            return;
        }

        addFavorite();
    };

    const handleFavoriteSuccess = (isFavorited: boolean) => {
        const key: keyof CultureResources = isFavorited
            ? "publicationPage-addedToFavorites"
            : "publicationPage-removedFromFavorites";
        return ToastManager.success(
            t(key, {
                codeAndEdition: publication.getDisplayCodeAndEdition(),
            })
        );
    };

    const {
        favorites,
        setFavorites,
        loading: loadingFavorites,
    } = usePublicationFavorites();

    const {
        addFavorite,
        unfavorite,
        isFavorited,
        loading: savingFavorite,
    } = useUpdatePublicationFavorite(
        publication,
        favorites,
        setFavorites,
        handleFavoriteSuccess
    );

    const favoriteButtonTooltip = isFavorited
        ? t("publicationPage-removeFromFavorites")
        : t("publicationPage-addToFavorites");
    const favoriteButtonTooltipDisabled = BrowserUtils.isTouchscreenDevice();
    const favoriteButtonIcon = isFavorited
        ? Icons.StarFilled
        : Icons.StarOutline;
    const favoriteButtonText = isFavorited ? t("favorited") : t("favorite");

    return (
        <>
            {(loadingFavorites || !publication.isPersisted()) && (
                <Loader
                    type={LoaderStyles.LinkGlyphGray}
                    size={LoaderSizes.Small}
                />
            )}
            {!loadingFavorites && publication.isPersisted() && (
                <Tooltip
                    content={favoriteButtonTooltip}
                    disabled={favoriteButtonTooltipDisabled}
                    placement={TooltipPlacement.Bottom}>
                    <Button
                        cssClassName={`${BASE_CLASSNAME}__metadata__top__right__favorite`}
                        disabled={savingFavorite}
                        onClick={handleFavoriteButtonClick}
                        style={ButtonStyles.None}
                        type={ButtonTypes.Button}>
                        <Icon type={favoriteButtonIcon} />
                        <span>{favoriteButtonText}</span>
                    </Button>
                </Tooltip>
            )}
        </>
    );
};

interface PublicationInfoProps {
    coverImageFile?: FileRecord;
    coverImageSrc?: string;
    edition: string;
    code: string;
    title: string;
    subTitle?: string;
}

export const PublicationInfo: React.FC<PublicationInfoProps> = ({
    coverImageFile,
    coverImageSrc,
    edition,
    code,
    title,
    subTitle,
}) => {
    return (
        <div className={`${BASE_CLASSNAME}__menu`}>
            {coverImageFile != null && (
                <div className={`${BASE_CLASSNAME}__cover`}>
                    <RemoteImage
                        file={coverImageFile}
                        loaderType={LoaderStyles.LinkGlyphGray}
                    />
                </div>
            )}

            {coverImageSrc != null && (
                <Image crossOrigin={true} src={coverImageSrc} />
            )}

            <div className={`${BASE_CLASSNAME}__identification`}>
                <div className={`${BASE_CLASSNAME}__edition`}>
                    <Paragraph size={ParagraphSizes.Large}>{edition}</Paragraph>
                </div>
                <Heading
                    cssClassName={`${BASE_CLASSNAME}__code`}
                    priority={HeadingPriority.Three}>
                    {`NFPA ${code}`}
                </Heading>
                <Heading
                    cssClassName={`${BASE_CLASSNAME}__title`}
                    priority={HeadingPriority.Five}>
                    {title}
                </Heading>
                <Paragraph
                    cssClassName={`${BASE_CLASSNAME}__subtitle`}
                    size={ParagraphSizes.Base}>
                    {subTitle}
                </Paragraph>
            </div>
        </div>
    );
};
